import React, { useEffect, useState } from "react";
import { Loader2, Check, X, Trash2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/adminApi";
import { useAuth } from "@/lib/auth";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal";
import ArchivedFilterToggle, { ArchivedRowBadge } from "@/components/ArchivedFilterToggle";

export default function AdminBooking() {
    const { user } = useAuth();
    const isAdminOrMaster = ["master_admin", "admin"].includes(user?.role);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [includeDeleted, setIncludeDeleted] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);

    const load = () => {
        setLoading(true);
        adminApi.booking({ include_deleted: includeDeleted || undefined })
            .then(setRows)
            .catch(() => toast.error("Couldn't load booking queue."))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [includeDeleted]);

    const update = async (id, body) => {
        try {
            await adminApi.patchBooking(id, body);
            toast.success("Updated.");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't update.");
        }
    };

    return (
        <div className="space-y-5" data-testid="admin-booking">
            <header className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <div className="mm-eyebrow">Booking</div>
                    <h1 className="mm-display text-3xl text-mm-navy">Consultation requests</h1>
                    <p className="mt-1 text-sm text-mm-muted">
                        Public booking form lands in a future phase — this queue is ready to receive it.
                    </p>
                </div>
                {isAdminOrMaster && (
                    <ArchivedFilterToggle
                        value={includeDeleted}
                        onChange={setIncludeDeleted}
                        testid="booking-archived-toggle"
                    />
                )}
            </header>
            <div className="mm-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-mm-bg text-[11px] uppercase tracking-wider text-mm-muted">
                            <tr>
                                <Th>Name</Th>
                                <Th>Contact</Th>
                                <Th>Requested window</Th>
                                <Th>Status</Th>
                                <Th>Confirmed slot</Th>
                                <Th>Created</Th>
                                <Th>Actions</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={7} className="py-10 text-center"><Loader2 className="mx-auto animate-spin text-mm-navy" /></td></tr>
                            ) : rows.length === 0 ? (
                                <tr><td colSpan={7} className="py-10 text-center text-mm-muted">No requests yet.</td></tr>
                            ) : (
                                rows.map((r) => {
                                    const archived = !!r.deleted_at;
                                    return (
                                    <tr key={r.id} className={`border-t border-mm-line ${archived ? "opacity-60" : ""}`} data-testid={`booking-row-${r.id}`}>
                                        <Td>
                                            <div className="flex items-center gap-2">
                                                <strong>{r.name}</strong>
                                                {archived && <ArchivedRowBadge deletedAt={r.deleted_at} testid={`booking-archived-badge-${r.id}`} />}
                                            </div>
                                        </Td>
                                        <Td>
                                            <div>{r.email}</div>
                                            <div className="text-xs text-mm-muted">{r.phone}</div>
                                        </Td>
                                        <Td>{r.requested_window}</Td>
                                        <Td>
                                            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] ${
                                                r.status === "confirmed" ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                                                : r.status === "denied" ? "border-rose-200 bg-rose-50 text-rose-800"
                                                : "border-amber-200 bg-amber-50 text-amber-900"
                                            }`}>{r.status}</span>
                                        </Td>
                                        <Td>
                                            <SlotInput
                                                initial={r.confirmed_slot || ""}
                                                onSave={(v) => update(r.id, { confirmed_slot: v })}
                                            />
                                        </Td>
                                        <Td className="text-mm-muted">{new Date(r.created_at).toLocaleDateString()}</Td>
                                        <Td>
                                            <div className="flex gap-1">
                                                {!archived && (
                                                    <>
                                                        <button
                                                            onClick={() => update(r.id, { status: "confirmed" })}
                                                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                                            title="Confirm"
                                                            data-testid={`booking-confirm-${r.id}`}
                                                        >
                                                            <Check size={13} />
                                                        </button>
                                                        <button
                                                            onClick={() => update(r.id, { status: "denied" })}
                                                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
                                                            title="Deny"
                                                            data-testid={`booking-deny-${r.id}`}
                                                        >
                                                            <X size={13} />
                                                        </button>
                                                    </>
                                                )}
                                                {isAdminOrMaster && !archived && (
                                                    <button
                                                        onClick={() => setPendingDelete({ id: r.id, name: r.name })}
                                                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-mm-line bg-white text-red-600 hover:bg-red-50"
                                                        title="Archive"
                                                        data-testid={`booking-delete-${r.id}`}
                                                    >
                                                        <Trash2 size={13} />
                                                    </button>
                                                )}
                                                {isAdminOrMaster && archived && (
                                                    <button
                                                        onClick={async () => {
                                                            try {
                                                                await adminApi.restore("booking", r.id);
                                                                toast.success("Restored.");
                                                                load();
                                                            } catch { toast.error("Couldn't restore."); }
                                                        }}
                                                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                                        title="Restore"
                                                        data-testid={`booking-restore-${r.id}`}
                                                    >
                                                        <RotateCcw size={13} />
                                                    </button>
                                                )}
                                            </div>
                                        </Td>
                                    </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <ConfirmDeleteModal
                open={!!pendingDelete}
                onClose={() => setPendingDelete(null)}
                onConfirm={async (reason) => {
                    try {
                        await adminApi.softDelete("booking", pendingDelete.id, reason);
                        toast.success("Booking archived. Restore within 30 days.");
                        setPendingDelete(null);
                        load();
                    } catch { toast.error("Couldn't archive."); }
                }}
                resourceLabel="booking"
                resourceName={pendingDelete?.name}
                testidPrefix="booking-confirm-delete"
            />
        </div>
    );
}

function Th({ children }) { return <th className="px-3 py-2.5 text-left font-medium">{children}</th>; }
function Td({ children, className = "" }) {
    return <td className={`px-3 py-2.5 align-top ${className}`}>{children}</td>;
}

function SlotInput({ initial, onSave }) {
    const [v, setV] = useState(initial);
    return (
        <div className="flex gap-1">
            <input
                value={v}
                onChange={(e) => setV(e.target.value)}
                placeholder="e.g. Thu 3pm ET"
                className="w-40 rounded-md border border-mm-line bg-white px-2 py-1 text-xs"
            />
            {v !== initial && (
                <button
                    onClick={() => onSave(v)}
                    className="rounded-md border border-mm-line bg-white px-2 text-xs hover:bg-mm-bg"
                >
                    Save
                </button>
            )}
        </div>
    );
}
